import {
  ActionIcon,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { ReactElement, useEffect, useRef } from "react";

const MAX_OPTIONS = 200;
const MAX_OPTION_LENGTH = 200;

interface OptionsEditorProps {
  onChange: (value: { label: string; value: string }[]) => void;
  value?: { label: string; value: string }[];
  initializeEmpty?: boolean;
}

export const OptionsEditor = ({
  onChange,
  value,
  initializeEmpty,
}: OptionsEditorProps): ReactElement => {
  const options = value ?? [];
  const initialized = useRef(false);

  useEffect(() => {
    if (
      initializeEmpty &&
      !initialized.current &&
      (!value || value.length === 0)
    ) {
      initialized.current = true;
      onChange([{ label: "", value: "" }]);
    }
  }, [initializeEmpty, value, onChange]);

  const addOption = () => {
    if (options.length >= MAX_OPTIONS) return;
    onChange([...options, { label: "", value: "" }]);
  };

  const removeOption = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  const updateOption = (
    index: number,
    field: "label" | "value",
    val: string,
  ) => {
    const updated = options.map((opt, i) =>
      i === index ? { ...opt, [field]: val } : opt,
    );
    onChange(updated);
  };

  return (
    <Stack gap="xs">
      {options.map((opt, index) => (
        <Group key={index} gap="xs" wrap="nowrap">
          <TextInput
            placeholder="Label"
            value={opt.label}
            onChange={(e) => updateOption(index, "label", e.target.value)}
            style={{ flex: 1 }}
            maxLength={MAX_OPTION_LENGTH}
          />
          <TextInput
            placeholder="Value"
            value={opt.value}
            onChange={(e) => updateOption(index, "value", e.target.value)}
            style={{ flex: 1 }}
            maxLength={MAX_OPTION_LENGTH}
          />
          <ActionIcon
            color="red"
            variant="subtle"
            onClick={() => removeOption(index)}
            disabled={options.length <= 1}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ))}
      {options.length >= MAX_OPTIONS && (
        <Text size="xs" c="dimmed">
          Maximum of {MAX_OPTIONS} options reached.
        </Text>
      )}
      <Button
        leftSection={<IconPlus size={14} />}
        variant="light"
        size="xs"
        onClick={addOption}
        disabled={options.length >= MAX_OPTIONS}
      >
        Add Option
      </Button>
    </Stack>
  );
};
